import { StatusTag } from "@/views/components/StatusTag";
import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Card, Form, Input, Select, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { ComponentProps, ComponentType } from "react";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import {
    Dialog as ZhDialog,
    InputNumber as ZhInputNumber,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import request from "@/api";
import { notifyOperationSuccess } from "@/utils/operationFeedback";
import { enterpriseManageService } from "@/api/services/enterpriseManageService";
import { prodTypeService } from "@/api/services/prodTypeService";
import { clearDaiBanState, getDaiBanState } from "@/utils";
import { useUserStore } from "@/views/stores";
import { TableImageCell } from "@/views/components/TableImageCell";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { dateTimeFormat } from "@/utils";
import { getEnterpriseName } from "@/utils/customer";
import { formatCurrency } from "@zhihaoscm/utils";
import AssignDialog, { type assignSubmitData, type Officer } from "@/views/components/AssignDialog/AssignDialog";
import type { ConsignmentPageResult, ConsignmentQuery, ConsignmentRow } from "./types";

type ConsignmentListQuery = Omit<ConsignmentQuery, "publishState"> & { publishState?: number[] };
type ConsignmentFilterOption = { label: string; value: number | string };

// 旧 Vue useTable 的默认分页大小为 10，重置查询沿用该契约。
const defaultQuery: ConsignmentListQuery = { page: 1, size: 10 };

// beta.13 的运行时支持 controls 配置，类型声明暂未暴露该兼容字段。
type ProductWeightInputProps = ComponentProps<typeof ZhInputNumber> & { controls?: boolean };
const ProductWeightInput = ZhInputNumber as ComponentType<ProductWeightInputProps>;
const productStateLabels: Record<number, string> = { 1: "上架", 2: "下架" };
const publishStateLabels: Record<number, string> = {
    1: "待审核",
    2: "未通过",
    3: "已发布",
    4: "已下架",
    5: "已关闭",
    6: "待提交"
};

type ConsignmentFilterValues = {
    keyword?: string;
    productTypeId?: string;
    supplierId?: number;
    handlerName?: string;
    state?: number;
    publishState?: number[];
};

type ConsignmentSpecialist = {
    user: { personId?: number; name?: string; employeeId?: string };
    productCount?: number;
};

export interface ConsignmentPageProps {
    assignConsignment?: (id: number | string, handlerId: number) => Promise<void>;
    closeConsignment?: (id: number | string, unActiveFileIds?: number[]) => Promise<void>;
    downShelf?: (id: number | string) => Promise<void>;
    findServiceSpecials?: (keyword: string) => Promise<ConsignmentSpecialist[]>;
    loadConsignments?: (query: ConsignmentListQuery) => Promise<ConsignmentPageResult>;
    loadProductTypeOptions?: (keyword: string) => Promise<ConsignmentFilterOption[]>;
    loadSupplierOptions?: (keyword: string) => Promise<ConsignmentFilterOption[]>;
    updateWeight?: (params: { id: number | string; weight: number }) => Promise<void>;
    permissions?: string[];
}

/** 复用 Vue 寄售商品分页接口。 */
export async function loadConsignments(query: ConsignmentListQuery): Promise<ConsignmentPageResult> {
    const response = await request.service.get<ConsignmentPageResult>("/admin/product-consignment/paging", {
        params: query
    });
    return response.data;
}

/** 复用 Vue SelectRemote 的寄售品类候选接口。 */
export async function loadConsignmentProductTypeOptions(keyword: string): Promise<ConsignmentFilterOption[]> {
    const rows = await prodTypeService.selectorInProduct({ keyword: keyword || undefined, productType: 1 });
    return rows.map(row => ({
        label: row.areaAbbreviation ? `${row.name} | ${row.areaAbbreviation}` : row.name,
        value: String(row.id)
    }));
}

/** 复用 Vue SelectRemote 的寄售供应商候选接口。 */
export async function loadConsignmentSupplierOptions(keyword: string): Promise<ConsignmentFilterOption[]> {
    const rows = await enterpriseManageService.selectorByProduct({ name: keyword || undefined, productType: 1 });
    return rows.map(row => ({ label: row.supplierName || "--", value: Number(row.id) }));
}

/** 读取待办带入的发布状态，保持 Vue init-param 的首次查询。 */
function getInitialConsignmentQuery(): ConsignmentListQuery {
    const publishState = getDaiBanState("consignPublishState").map(Number).filter(Number.isFinite);
    return publishState.length ? { ...defaultQuery, publishState } : defaultQuery;
}

/** 复用 Vue 寄售商品权重更新接口。 */
export async function updateConsignmentWeight(params: { id: number | string; weight: number }): Promise<void> {
    await request.service.put(`/admin/product-consignment/weight/${params.id}`, params);
}

/** 复用 Vue 商品下架接口。 */
export async function downShelfConsignment(id: number | string): Promise<void> {
    await request.service.put(`/admin/product-consignment/down-shelf/${id}`);
}

/** 复用 Vue 商品关闭接口，并提交需要解除关联的文件 ID。 */
export async function closeConsignment(id: number | string, unActiveFileIds: number[] = []): Promise<void> {
    await request.service.put(`/admin/product-consignment/close/${id}`, { unActiveFileIds });
}

/** 复用 Vue 专员候选查询接口。 */
export async function findServiceSpecials(keyword: string): Promise<ConsignmentSpecialist[]> {
    const response = await request.service.get<ConsignmentSpecialist[]>(
        `/admin/product-consignment/find/specials?name=${encodeURIComponent(keyword)}`
    );
    return response.data;
}

/** 复用 Vue 寄售商品指派接口。 */
export async function assignConsignment(id: number | string, handlerId: number): Promise<void> {
    await request.service.get(`/admin/product-consignment/assign/${id}/${handlerId}`);
}

/** 将 Vue 寄售商品列表返回的状态枚举统一为 React 操作和文案使用的数字。 */
function normalizeConsignmentRows(rows?: ConsignmentRow[]): ConsignmentRow[] {
    return (rows || []).map(row => ({
        ...row,
        productConsignment: {
            ...row.productConsignment,
            state: row.productConsignment.state == null ? undefined : Number(row.productConsignment.state),
            publishState:
                row.productConsignment.publishState == null ? undefined : Number(row.productConsignment.publishState)
        }
    }));
}

/** 寄售商品列表，完整保留 Vue 筛选、列、权限和状态驱动操作。 */
export function ConsignmentPage({
    assignConsignment: assign = assignConsignment,
    closeConsignment: close = closeConsignment,
    downShelf: removeFromShelf = downShelfConsignment,
    findServiceSpecials: findSpecials = findServiceSpecials,
    loadConsignments: load = loadConsignments,
    loadProductTypeOptions: loadProductTypes = loadConsignmentProductTypeOptions,
    loadSupplierOptions: loadSuppliers = loadConsignmentSupplierOptions,
    updateWeight: saveWeight = updateConsignmentWeight,
    permissions
}: ConsignmentPageProps) {
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    const currentPermissions = permissions || storePermissions;
    const canDeal = currentPermissions.includes("ROLE_PRODUCT_DEAL");
    const canManage = currentPermissions.includes("ROLE_PRODUCT_MANAGE");
    const canClosePermission = currentPermissions.includes("ROLE_PRODUCT_CLOSE");
    const [items, setItems] = useState<ConsignmentRow[]>([]);
    const [total, setTotal] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [assignTarget, setAssignTarget] = useState<ConsignmentRow | null>(null);
    const [specialists, setSpecialists] = useState<ConsignmentSpecialist[]>([]);
    const [selectedHandlerId, setSelectedHandlerId] = useState<number | undefined>();
    const [assignError, setAssignError] = useState<string | null>(null);
    const [assignLoading, setAssignLoading] = useState(false);
    const [query, setQuery] = useState<ConsignmentListQuery>(getInitialConsignmentQuery);
    const [editingWeightId, setEditingWeightId] = useState<number | string | null>(null);
    const [editingWeightValue, setEditingWeightValue] = useState<number | string>("");
    const [filterForm] = Form.useForm<ConsignmentFilterValues>();
    const [productTypeOptions, setProductTypeOptions] = useState<ConsignmentFilterOption[]>([]);
    const [productTypeLoading, setProductTypeLoading] = useState(false);
    const [supplierOptions, setSupplierOptions] = useState<ConsignmentFilterOption[]>([]);
    const [supplierLoading, setSupplierLoading] = useState(false);
    const [downShelfTargetId, setDownShelfTargetId] = useState<number | string | null>(null);
    const [downShelfLoading, setDownShelfLoading] = useState(false);
    const [downShelfError, setDownShelfError] = useState<string | null>(null);

    const refresh = async (targetQuery: ConsignmentListQuery = query) => {
        const result = await load(targetQuery);
        setItems(normalizeConsignmentRows(result.list));
        setTotal(result.total || 0);
    };

    useEffect(() => {
        let disposed = false;
        void load(query)
            .then(result => {
                if (!disposed) {
                    setErrorMessage(null);
                    setItems(normalizeConsignmentRows(result.list));
                    setTotal(result.total || 0);
                }
            })
            .catch(() => {
                if (!disposed) {
                    setErrorMessage("寄售商品加载失败");
                    setItems([]);
                    setTotal(0);
                }
            });
        return () => {
            disposed = true;
        };
    }, [load, query]);

    // 列表下架沿用 Vue 的二次确认，确认前不能触发下架接口。
    const openDownShelf = (id?: number | string) => {
        if (id == null) return;
        setDownShelfTargetId(id);
        setDownShelfError(null);
    };

    const cancelDownShelf = () => {
        setDownShelfTargetId(null);
        setDownShelfError(null);
    };

    const confirmDownShelf = async () => {
        if (downShelfTargetId == null) return;
        setDownShelfLoading(true);
        try {
            await removeFromShelf(downShelfTargetId);
            await refresh();
            cancelDownShelf();
        } catch {
            // 下架接口或刷新失败时保留确认弹窗，并反馈 Vue 页面同类操作错误。
            setDownShelfError("商品下架失败");
        } finally {
            setDownShelfLoading(false);
        }
    };

    const handleClose = async (row: ConsignmentRow) => {
        const id = row.productConsignment.id;
        if (id == null) return;
        // Vue 关闭接口要求清理主图、视频和附加图片的文件 ID。
        const unActiveFileIds = [
            row.mainFile?.id,
            row.videoFile?.id,
            ...(row.otherImgFile || []).map(file => file.id)
        ].filter((fileId): fileId is number => fileId != null);
        try {
            await close(id, unActiveFileIds);
            await refresh();
        } catch {
            // 关闭接口或刷新失败时保留当前商品列表，并反馈 Vue 页面同类操作错误。
            setErrorMessage("商品关闭失败");
        }
    };

    const searchSpecialists = async (keyword: string) => {
        setAssignLoading(true);
        try {
            setAssignError(null);
            const nextSpecialists = await findSpecials(keyword);
            setSpecialists(nextSpecialists);
            return nextSpecialists;
        } catch {
            setAssignError("专员查询失败");
            setSpecialists([]);
        } finally {
            setAssignLoading(false);
        }
    };

    const openAssign = (row: ConsignmentRow) => {
        setAssignTarget(row);
        setSelectedHandlerId(row.productConsignment.handlerId);
        void searchSpecialists("");
    };

    const handleAssign = async ({ officerId }: assignSubmitData) => {
        if (!assignTarget || officerId == null) {
            setAssignError("请选择指派的服务人员");
            return;
        }
        const id = assignTarget.productConsignment.id;
        if (id == null) return;
        setAssignLoading(true);
        try {
            await assign(id, officerId);
            notifyOperationSuccess("变更指派成功");
            setAssignTarget(null);
            await refresh();
        } catch (error) {
            setAssignError("变更指派失败");
            throw error;
        } finally {
            setAssignLoading(false);
        }
    };

    // 将旧 Vue 七项筛选转换为寄售分页接口参数，并同步清理待办状态。
    const submitFilters = (values: ConsignmentFilterValues) => {
        const publishState = values.publishState?.map(Number).filter(Number.isFinite);
        clearDaiBanState(publishState || [], "consignPublishState");
        setErrorMessage(null);
        setQuery({
            ...defaultQuery,
            ...(values.keyword?.trim() ? { keyword: values.keyword.trim() } : {}),
            ...(values.productTypeId ? { productTypeId: values.productTypeId } : {}),
            ...(values.supplierId ? { supplierId: Number(values.supplierId) } : {}),
            ...(values.state ? { state: Number(values.state) } : {}),
            ...(publishState?.length ? { publishState } : {}),
            ...(values.handlerName?.trim() ? { handlerName: values.handlerName.trim() } : {})
        });
    };

    // 清空全部筛选和待办缓存，并恢复旧 Vue 默认分页参数。
    const resetFilters = () => {
        clearDaiBanState([], "consignPublishState");
        filterForm.resetFields();
        filterForm.setFieldValue("publishState", []);
        setProductTypeOptions([]);
        setSupplierOptions([]);
        setQuery(defaultQuery);
    };

    /** 按 Vue SelectRemote 契约查询寄售品类候选。 */
    const searchProductTypes = async (keyword: string) => {
        setProductTypeLoading(true);
        try {
            setProductTypeOptions(await loadProductTypes(keyword.trim()));
        } catch {
            setProductTypeOptions([]);
        } finally {
            setProductTypeLoading(false);
        }
    };

    /** 按 Vue SelectRemote 契约查询寄售供应商候选。 */
    const searchSuppliers = async (keyword: string) => {
        setSupplierLoading(true);
        try {
            setSupplierOptions(await loadSuppliers(keyword.trim()));
        } catch {
            setSupplierOptions([]);
        } finally {
            setSupplierLoading(false);
        }
    };

    /** 将 Vue 两个自定义排序列映射为寄售分页接口字段。 */
    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
            const sortKeyMap: Record<string, string> = {
                "productConsignment.weight": "weight",
                updatedTime: "updated_time"
            };
            if (!prop || !sortKeyMap[prop]) return;
            setQuery(currentQuery => {
                const nextQuery = { ...currentQuery, page: 1 };
                if (!order) {
                    delete nextQuery.sortKey;
                    delete nextQuery.sortOrder;
                } else {
                    nextQuery.sortKey = sortKeyMap[prop];
                    nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                }
                return nextQuery;
            });
        },
        []
    );

    // 用 zhihao-ui-react 列契约承接 Vue 寄售商品的嵌套字段、状态枚举和权限动作。
    const columns: TableColumnProps<ConsignmentRow>[] = [
        {
            key: "id",
            label: "商品编号",
            width: 176,
            prop: "productConsignment.id",
            render: ({ row }) => (
                <Link
                    className="consignment-product-id"
                    to={`/productManagement/consignment/detail?id=${row.productConsignment.id || ""}&time=${Date.now()}`}
                >
                    {row.productConsignment.id || "--"}
                </Link>
            )
        },
        {
            key: "weight",
            label: "权重值",
            width: 176,
            // 对齐 Vue `productConsignment.weight` 的 custom 排序字段。
            prop: "productConsignment.weight",
            sortable: true,
            render: ({ row }) => {
                const id = row.productConsignment.id;
                if (id != null && editingWeightId === id) {
                    return (
                        <Space.Compact className="admin-table-inline-editor consignment-weight-editor">
                            <ProductWeightInput
                                aria-label="寄售商品权重值"
                                autoFocus
                                controls={false}
                                max={10000}
                                min={0}
                                precision={0}
                                value={editingWeightValue}
                                onChange={setEditingWeightValue}
                            />
                            <Button
                                aria-label="保存寄售商品权重"
                                size="small"
                                type="link"
                                onClick={async () => {
                                    const weight = Number(editingWeightValue);
                                    if (editingWeightValue === "" || !Number.isFinite(weight)) return;
                                    try {
                                        await saveWeight({ id, weight });
                                        // 保存成功后立即回写当前行，避免必须刷新列表才能看到新权重。
                                        setItems(current =>
                                            current.map(item =>
                                                item.productConsignment.id === id
                                                    ? {
                                                          ...item,
                                                          productConsignment: { ...item.productConsignment, weight }
                                                      }
                                                    : item
                                            )
                                        );
                                        setEditingWeightId(null);
                                    } catch {
                                        setErrorMessage("权重保存失败");
                                    }
                                }}
                            >
                                保存
                            </Button>
                            <Button
                                aria-label="取消寄售商品权重"
                                size="small"
                                type="link"
                                onClick={() => setEditingWeightId(null)}
                            >
                                取消
                            </Button>
                        </Space.Compact>
                    );
                }
                return (
                    <Space size={4}>
                        <span>{row.productConsignment.weight ?? 0}</span>
                        {id != null ? (
                            <Button
                                aria-label="编辑寄售商品权重值"
                                icon={<i aria-hidden="true" className="iconfont icon-edit" />}
                                size="small"
                                type="link"
                                onClick={() => {
                                    setEditingWeightValue(row.productConsignment.weight ?? 0);
                                    setEditingWeightId(id);
                                }}
                            />
                        ) : null}
                    </Space>
                );
            }
        },
        {
            key: "title",
            label: "主图/标题",
            minWidth: 176,
            prop: "imgMainId",
            render: ({ row }) => (
                <TableImageCell
                    alt="寄售商品主图"
                    src={row.mainFile?.path}
                    title={row.productConsignment.title || "--"}
                />
            )
        },
        {
            key: "productType",
            label: "品类",
            minWidth: 120,
            prop: "productTypeId",
            render: ({ row }) =>
                row.productType?.name
                    ? `${row.productType.name}${row.productType.areaAbbreviation ? ` (${row.productType.areaAbbreviation})` : ""}`
                    : "--"
        },
        {
            key: "supplier",
            label: "供应商",
            minWidth: 120,
            prop: "supplierId",
            render: ({ row }) =>
                row.productConsignment.supplierEnterprise?.enterpriseName ||
                row.productConsignment.supplierEnterprise?.supplierName ||
                getEnterpriseName(row.productConsignment.supplierEnterprise as never)
        },
        {
            key: "unitPrice",
            label: "销售单价 (元/吨)",
            width: 204,
            prop: "productConsignment.unitPrice",
            align: "right",
            headerAlign: "right",
            render: ({ row }) => {
                const { priceType, unitPrice } = row.productConsignment;
                if (!unitPrice) return "--";
                return `${formatCurrency(unitPrice)}${priceType === 2 ? "（预估价格）" : ""}`;
            }
        },
        {
            key: "minOrderTon",
            label: "起订吨数 (吨)",
            width: 120,
            prop: "productConsignment.minOrderTon",
            align: "right",
            headerAlign: "right",
            render: ({ row }) =>
                row.productConsignment.minOrderTon ? formatCurrency(row.productConsignment.minOrderTon) : "--"
        },
        {
            key: "inventory",
            label: "库存量（吨）",
            width: 148,
            prop: "progress",
            align: "right",
            headerAlign: "right",
            showOverflowTooltip: false,
            render: ({ row }) => {
                const inventoryValue = row.productConsignment.inventoryQuantity;
                const inventory = Number(inventoryValue || 0);
                const initialInventory = Number(row.productConsignment.initInventoryQuantity || 0);
                const sold = initialInventory - inventory;
                const percent = initialInventory ? Number(((sold / initialInventory) * 100).toFixed(2)) : 0;
                // Vue InventoryProgress places remaining stock above the sold progress bar.
                return (
                    <div className="consignment-inventory-progress">
                        <div className="consignment-inventory-progress__amount">
                            {inventoryValue == null ? "--" : formatCurrency(inventoryValue)}
                        </div>
                        <div className="consignment-inventory-progress__row">
                            <span className="consignment-inventory-progress__label">已售</span>
                            <div
                                aria-label="已售进度"
                                aria-valuemax={100}
                                aria-valuemin={0}
                                aria-valuenow={percent}
                                className={`consignment-inventory-progress__track${percent < 20 ? " is-invert" : ""}`}
                                role="progressbar"
                            >
                                <div className="consignment-inventory-progress__fill" style={{ width: `${percent}%` }}>
                                    <span>{percent}%</span>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            }
        },
        {
            key: "updatedTime",
            label: "更新时间",
            width: 150,
            prop: "updatedTime",
            sortable: true,
            render: ({ row }) => dateTimeFormat(row.productConsignment.updatedTime)
        },
        {
            key: "handlerName",
            label: "商品专员",
            width: 100,
            prop: "productConsignment.handlerName",
            render: ({ row }) => row.productConsignment.handlerName || "--"
        },
        {
            key: "state",
            label: "商品状态",
            width: 90,
            prop: "productConsignment.state",
            render: ({ row }) => <StatusTag>{productStateLabels[row.productConsignment.state || 0] || "--"}</StatusTag>
        },
        {
            key: "publishState",
            label: "发布状态",
            width: 90,
            prop: "productConsignment.publishState",
            render: ({ row }) => (
                <StatusTag>{publishStateLabels[row.productConsignment.publishState || 0] || "--"}</StatusTag>
            )
        },
        {
            key: "operation",
            label: "操作",
            prop: "operation",
            fixed: "right",
            width: 230,
            render: ({ row }) => {
                const id = row.productConsignment.id;
                const canAudit = canManage && row.productConsignment.publishState === 1;
                const canAssign =
                    canManage &&
                    !(
                        row.productConsignment.state === 2 &&
                        [4, 5, 6].includes(row.productConsignment.publishState || 0)
                    );
                const canEdit =
                    canDeal &&
                    row.productConsignment.state === 2 &&
                    [2, 4, 6].includes(row.productConsignment.publishState || 0);
                const canClose =
                    canClosePermission &&
                    row.productConsignment.state === 2 &&
                    [1, 2, 4, 6].includes(row.productConsignment.publishState || 0);
                const canDown =
                    canDeal &&
                    row.productConsignment.state === 1 &&
                    [2, 3].includes(row.productConsignment.publishState || 0);
                // Vue 列表按状态和权限组合展示可执行操作。
                if (!canAssign && !canAudit && !canEdit && !canClose && !canDown) return null;
                return (
                    <Space>
                        {canAssign ? (
                            <Button
                                aria-label="变更指派"
                                disabled={banButton}
                                size="small"
                                type="link"
                                onClick={() => openAssign(row)}
                            >
                                变更指派
                            </Button>
                        ) : null}
                        {canAudit ? (
                            <Button
                                aria-label="审核商品"
                                disabled={banButton}
                                size="small"
                                type="link"
                                onClick={() =>
                                    void navigate(
                                        `/productManagement/consignment/audit?id=${id || ""}&time=${Date.now()}`
                                    )
                                }
                            >
                                审核
                            </Button>
                        ) : null}
                        {canEdit ? (
                            <Button
                                aria-label="编辑商品"
                                disabled={banButton}
                                size="small"
                                type="link"
                                onClick={() =>
                                    void navigate(
                                        `/productManagement/consignment/edit?id=${id || ""}&time=${Date.now()}`
                                    )
                                }
                            >
                                编辑
                            </Button>
                        ) : null}
                        {canClose ? (
                            <Button
                                aria-label="关闭商品"
                                disabled={banButton}
                                size="small"
                                type="link"
                                onClick={() => void handleClose(row)}
                            >
                                关闭
                            </Button>
                        ) : null}
                        {canDown ? (
                            <Button
                                aria-label="下架商品"
                                disabled={banButton}
                                size="small"
                                type="link"
                                onClick={() => openDownShelf(id)}
                            >
                                下架
                            </Button>
                        ) : null}
                    </Space>
                );
            }
        }
    ];

    return (
        <section aria-label="consignment-page" className="flex h-full min-h-full flex-col">
            <ZhDialog
                open={downShelfTargetId != null}
                title="提示"
                width={400}
                onCancel={cancelDownShelf}
                footer={
                    <Space>
                        <Button disabled={downShelfLoading} onClick={cancelDownShelf}>
                            取消
                        </Button>
                        <Button
                            aria-label="确认下架"
                            loading={downShelfLoading}
                            type="primary"
                            onClick={() => void confirmDownShelf()}
                        >
                            确认
                        </Button>
                    </Space>
                }
            >
                <p>是否确认下架</p>
                {downShelfError ? <Alert message={downShelfError} role="alert" type="error" /> : null}
            </ZhDialog>
            <AssignDialog
                open={Boolean(assignTarget)}
                info={assignTarget || undefined}
                title="变更指派商品专员"
                confirmText="确认"
                officers={specialists as Officer[]}
                searchSpecial={searchSpecialists}
                tips="跟进的商品数量"
                countFiled="productCount"
                countSuffix="个"
                curOfficerId={selectedHandlerId}
                loading={assignLoading}
                error={assignError}
                onClose={() => {
                    setAssignTarget(null);
                    setAssignError(null);
                }}
                onCurOfficerIdChange={setSelectedHandlerId}
                onSubmit={handleAssign}
            />
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<ConsignmentRow>
                    data={items}
                    columns={normalizeOperationColumns(columns, items)}
                    pagination={false}
                    rowKey={row => String(row.productConsignment.id)}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                寄售商品管理
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Form
                                className="admin-table-filter-form mb-4"
                                form={filterForm}
                                initialValues={{ publishState: query.publishState || [] }}
                                layout="inline"
                                onFinish={values => submitFilters(values)}
                                onValuesChange={(changedValues, values) => {
                                    // Vue SearchFormItem 在任一筛选清空后立即查询；输入内容本身仍由搜索按钮提交。
                                    const clearedFilter = Object.values(changedValues).some(
                                        value =>
                                            value === "" ||
                                            value == null ||
                                            (Array.isArray(value) && value.length === 0)
                                    );
                                    if (clearedFilter) submitFilters(values);
                                }}
                            >
                                {canDeal ? (
                                    <Button
                                        aria-label="新增寄售商品"
                                        className="admin-table-primary-action"
                                        disabled={banButton}
                                        type="primary"
                                        onClick={() =>
                                            void navigate(`/productManagement/consignment/add?time=${Date.now()}`)
                                        }
                                    >
                                        新增商品
                                    </Button>
                                ) : null}
                                <Form.Item className="product-filter-keyword" name="keyword">
                                    <Input allowClear placeholder="请输入商品编号或商品标题" />
                                </Form.Item>
                                <Form.Item className="product-filter-category" name="productTypeId">
                                    <Select
                                        allowClear
                                        aria-label="寄售品类"
                                        filterOption={false}
                                        loading={productTypeLoading}
                                        options={productTypeOptions}
                                        placeholder="请选择品类"
                                        showSearch
                                        onSearch={value => void searchProductTypes(value)}
                                    />
                                </Form.Item>
                                <Form.Item className="product-filter-supplier" name="supplierId">
                                    <Select
                                        allowClear
                                        aria-label="寄售供应商"
                                        filterOption={false}
                                        loading={supplierLoading}
                                        options={supplierOptions}
                                        placeholder="请选择供应商"
                                        showSearch
                                        onSearch={value => void searchSuppliers(value)}
                                    />
                                </Form.Item>
                                <Form.Item className="product-filter-state" name="state">
                                    <Select
                                        allowClear
                                        aria-label="商品状态"
                                        placeholder="请选择商品状态"
                                        style={{ width: 134 }}
                                        options={Object.entries(productStateLabels).map(([value, label]) => ({
                                            label,
                                            value: Number(value)
                                        }))}
                                    />
                                </Form.Item>
                                <Form.Item className="product-filter-publish-state" name="publishState">
                                    <Select
                                        allowClear
                                        aria-label="发布状态"
                                        maxTagCount={1}
                                        mode="multiple"
                                        placeholder="请选择发布状态"
                                        showSearch={false}
                                        style={{ width: 134 }}
                                        options={Object.entries(publishStateLabels).map(([value, label]) => ({
                                            label,
                                            value: Number(value)
                                        }))}
                                        onChange={values => clearDaiBanState(values, "consignPublishState")}
                                    />
                                </Form.Item>
                                <Form.Item className="product-filter-handler" name="handlerName">
                                    <Input allowClear placeholder="请输入商品专员搜索" />
                                </Form.Item>
                                <Button
                                    aria-label="查询商品"
                                    htmlType="submit"
                                    icon={<TableSearchIcon name="search" />}
                                    type="primary"
                                >
                                    搜索
                                </Button>
                                <Button
                                    aria-label="重置"
                                    className="table-filter-reset-button"
                                    icon={<TableSearchIcon name="reset" />}
                                    title="重置"
                                    onClick={resetFilters}
                                />
                            </Form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                showTotal={value => `共${value}条数据`}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                // 沿用 Vue 寄售商品分页契约，切换页大小后回到第 1 页。
                                onChange={(page, pageSize) =>
                                    setQuery(currentQuery => ({
                                        ...currentQuery,
                                        page: pageSize !== currentQuery.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default ConsignmentPage;
